import { Extent } from 'ol/extent'
import Projection from 'ol/proj/Projection'
import Layer from 'ol/layer/Layer'
import View from 'ol/View'
import OLMap from 'ol/Map'
import Overlay from 'ol/Overlay'
import Feature, { FeatureLike } from 'ol/Feature'
import Geometry from 'ol/geom/Geometry'
import { ToolType } from './tools/Tool'
import VectorSource from 'ol/source/Vector'
import VectorLayer from 'ol/layer/Vector'
// @ts-ignore
import ContextMenu from 'ol-contextmenu'
import LineString from 'ol/geom/LineString'
import { Nullable } from '@/shared/types/app.types'

export enum KernelType {
    NONE = 'NONE',
    SHARPEN = 'SHARPEN',
    SHARPENLESS = 'SHARPENLESS',
    BLUR = 'BLUR',
    SHADOW = 'SHADOW',
    EMBOSS = 'EMBOSS',
    EDGE = 'EDGE'
}

export interface ViewerUserConfiguration {
    showAnnotations: boolean
    defaultColor: string
    showTitle: boolean
    showScale: boolean
    showAnnotationWindow: boolean
}

export enum ImageType {
    MACRO= 'MACRO',
    MICRO= 'MICRO',
    WSI = 'WSI'
}

export interface ImageInfo {
    width: number
    height: number
    length: number
    type: ImageType
    url: string
    filename: string
    label: string
    mimetype: string
    scale: number // scale in mm
}

export interface WMTSImageInfo {
    headers: Map<string, string>
    filename: string
    imageHeight: number
    imageWidth: number
    length: number
    objective: number
    vendor: string
}

export interface IViewerState {
    label: string
    imageInfo: ImageInfo
    wmtsImageInfo: WMTSImageInfo
    extent: Nullable<Extent>
    imageExtent: Nullable<Extent>
    extentWidth: number
    resolutions: Nullable<Array<number>>
    matrixIds: Nullable<string[]>
    projection: Nullable<Projection>
    imageLayer: Nullable<Layer<any>>
    view: Nullable<View>
    map: Nullable<OLMap>
    helpTooltipElement: Nullable<HTMLDivElement>
    helpTooltip: Nullable<Overlay>
    annotationId: string
    annotations: Nullable<Feature<Geometry>[]>
    selectedTool: Nullable<ToolType>
    vectorSource: Nullable<VectorSource<Geometry>>
    vectorLayer: Nullable<VectorLayer<VectorSource<Geometry>>>
    tooltipLayer: Nullable<VectorLayer<VectorSource<Geometry>>>
    contextMenu: Nullable<ContextMenu>
    measureTooltipElement: Nullable<HTMLDivElement>
    measureTooltip: Nullable<Overlay>
    showMap: boolean
    showFx: boolean
    zoomLabel: string
    userConfiguration: ViewerUserConfiguration
    selectColor: string
    selectedKernel: KernelType
    selectedBrightness: number
    selectedContrast: number
}
export const initialViewerState =
    (): IViewerState => ({
        resolutions: null,
        projection: null,
        view: null,
        map: null,
        matrixIds: null,
        helpTooltip: null,
        helpTooltipElement: null,
        selectedTool: null,
        contextMenu: null,
        measureTooltip: null,
        measureTooltipElement: null,
        imageLayer: null,
        annotations: null,
        extent: null,
        imageExtent: null,
        label: '',
        extentWidth: -1,
        annotationId: '',
        selectColor: 'rgb(77, 77, 218)',
        vectorSource: null,
        vectorLayer: null,
        tooltipLayer: null,
        showMap: true,
        showFx: false,
        zoomLabel: '0.00',
        userConfiguration: {
            showAnnotations: true,
            defaultColor: 'rgb(77, 77, 218)',
            showTitle: true,
            showScale: true,
            showAnnotationWindow: true
        },
        imageInfo: {
            width: 0,
            height: 0,
            type: ImageType.MACRO,
            mimetype: '',
            url: '',
            length: 0,
            filename: '',
            label: '',
            scale: -1
        },
        wmtsImageInfo: {
            vendor: '',
            objective: 20,
            headers: new Map<string, string>(),
            filename: '',
            imageHeight: 0,
            imageWidth: 0,
            length: 0
        },
        selectedKernel: KernelType.NONE,
        selectedBrightness: 0,
        selectedContrast: 1
    })

export interface ContextMenuItem {
    text: string
    classname?: string
    callback?: Function
    items?: ContextMenuItem[]
}

export interface FeaturesManager {
    loadFeatures(): Promise<any>
    saveFeatures(annotationId: string, attachmentFilename: string, geojsonFeatures: string, imageBytes: string, thumbnailBytes: string): Promise<string | undefined>
    saveCurrentMeasureScale(): Promise<void>
}

export interface ConfigurationManager {
    loadConfiguration(): Promise<ViewerUserConfiguration>
    saveConfiguration(configuration: ViewerUserConfiguration): void
}

export interface ToolTipManager {
    removeToolTip(feature: FeatureLike): void
    createFixedToolTip(feature: Feature<Geometry>): void
    getFixedToolTipText(feature: Feature<Geometry>): string
}

export enum PanDirection {
    LEFT='LEFT',
    RIGHT='RIGHT',
    TOP='TOP',
    BOTTOM='BOTTOM',
}

export enum UnitType {
    M = 'm',
    DM = 'dm',
    CM = 'cm',
    MM = 'mm',
    MICRAS = 'µm',
    NM = 'nm'
}

export interface ScaleInfo {
    scale: number
    unit: UnitType
}

export interface AnnotationManager {
    recalculateAllAreasAndDistances(recreateTooltips?: boolean): void
    recalculateAreaAndDistance(feature: Feature<Geometry>, recreateTooltips?: boolean): void
    applyAnnotationDetails(feature: Feature<Geometry>, showModal: boolean): Promise<void>
    askScale(lineFeature: Feature<LineString>): Promise<ScaleInfo>
    saveCurrentMeasureScale(): Promise<void>
    notifyChangesMade(): void
}
